import Button from '@fieldmargin/webapp-styling/components/button';
import BillingPeriodSwitcher, {
    BillingPeriod,
} from '@fieldmargin/webapp-styling/components/pricing/BillingPeriodSwitcher';
import EssentialsPricingColumn from '@fieldmargin/webapp-styling/components/pricing/columns/EssentialsPricingColumn';
import FreePricingColumn from '@fieldmargin/webapp-styling/components/pricing/columns/FreePricingColumn';
import PlusPricingColumn from '@fieldmargin/webapp-styling/components/pricing/columns/PlusPricingColumn';
import ProPricingColumn from '@fieldmargin/webapp-styling/components/pricing/columns/ProPricingColumn';
import MultiFarmMessage from '@fieldmargin/webapp-styling/components/pricing/MultiFarmMessage';
import {
    getCurrencySymbol,
    Prices,
    PricingMap,
} from '@fieldmargin/webapp-styling/components/pricing/pricing-utils';
import PricingColumns from '@fieldmargin/webapp-styling/components/pricing/PricingColumns';
import PricingComparison from '@fieldmargin/webapp-styling/components/pricing/PricingComparison';
import { UserLocationObj } from 'components/user-location/UserLocationContext';
import { graphql, Link, useStaticQuery } from 'gatsby';
import React, { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trackLink } from '../../util/Segment';
import EssentialsInfo from '../plan-info/EssentialsInfo';
import FreeInfo from '../plan-info/FreeInfo';
import PlusInfo from '../plan-info/PlusInfo';
import ProInfo from '../plan-info/ProInfo';
import { getCountryCurrency } from './pricing-utils';
import './PricingTable.css';
import ReactMarkdown from 'react-markdown';

interface FreeLinkProps
    extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement> {
    place: string;
}
const FreeLink = ({ place, ...props }: FreeLinkProps) => {
    const { t } = useTranslation();

    return (
        <Button
            as="a"
            href="https://notes.fieldmargin.com"
            rel="noopener noreferrer"
            target="_blank"
            onClick={trackLink(`Free get started button clicked in ${place}`)}
            className="PricingLink"
            {...props}
        >
            {t('onboard_start')}
        </Button>
    );
};
const MobileFreeLink = ({ place, ...props }: FreeLinkProps) => {
    const { t } = useTranslation();

    return (
        <a
            href="https://notes.fieldmargin.com"
            rel="noopener noreferrer"
            target="_blank"
            onClick={trackLink(`Free get started button clicked on ${place}`)}
            className="MobilePricingLink"
            {...props}
        >
            {t('onboard_start')}
        </a>
    );
};

interface PlanLinkProps
    extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement> {
    place: string;
    plan: string;
}
const PlanLink = ({ place, plan, ...props }: PlanLinkProps) => {
    const { t } = useTranslation();
    return (
        <Button
            as="a"
            href="https://notes.fieldmargin.com/?redirect=billing%2Fupgrade"
            rel="noopener noreferrer"
            target="_blank"
            onClick={trackLink(`${plan} get started button clicked in ${place}`)}
            className="PricingLink"
            {...props}
        >
            {t('onboard_start')}
        </Button>
    );
};
const MobilePlanLink = ({ place, plan, ...props }: PlanLinkProps) => {
    const { t } = useTranslation();

    return (
        <a
            href="https://notes.fieldmargin.com/?redirect=billing%2Fupgrade"
            rel="noopener noreferrer"
            target="_blank"
            onClick={trackLink(`${plan} get started button clicked in ${place}`)}
            className="MobilePricingLink"
            {...props}
        >
            {t('onboard_start')}
        </a>
    );
};

interface Plan {
    uuid: string;
    interval: BillingPeriod;
    currency: string;
    priceInCents: number;
}

interface Product {
    uuid: string;
    name: string;
    description: string;
    plans: Plan[];
}

export default ({ userLocation }: { userLocation: UserLocationObj | null }) => {
    const { t } = useTranslation();
    const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('annually');

    const productsQuery = useStaticQuery<{ products: { data: Product[] } }>(graphql`
        query {
            products {
                data {
                    uuid
                    name
                    description
                    plans {
                        uuid
                        currency
                        priceInCents
                        interval
                    }
                }
            }
        }
    `);

    let billingPeriodContent: ReactNode = null;
    let mainContent = (
        <p style={{ textAlign: 'center' }}>
            <em>{t('loading')}</em>
        </p>
    );

    if (userLocation) {
        const currency = getCountryCurrency(userLocation.country.iso_code);
        const prices = productsQuery.products.data.reduce((pricingMap, product) => {
            let price =
                (
                    product.plans.find(
                        (plan) => plan.interval === billingPeriod && plan.currency === currency
                    ) as Plan
                ).priceInCents / 100;

            if (billingPeriod === 'annually') {
                price = price / 12;
            }

            if (product.name === 'ESSENTIALS_PLAN') {
                pricingMap.essentials = { [currency]: { original: price } };
            } else if (product.name === 'PLUS_PLAN') {
                pricingMap.plus = { [currency]: { original: price } };
            } else if (product.name === 'PRO_PLAN') {
                pricingMap.pro = { [currency]: { original: price } };
            }
            return pricingMap;
        }, {} as PricingMap);

        const maxSaving = Number(
            (
                productsQuery.products.data.find(
                    (product) => product.name === 'PRO_PLAN'
                ) as Product
            ).plans
                // Get only relevant plans
                .filter((plan) => plan.currency === currency)
                // Map to price per year in pounds
                .map(
                    (plan) =>
                        (plan.interval === 'monthly' ? plan.priceInCents * 12 : plan.priceInCents) /
                        100
                )
                // Sort ascendingly
                .sort()
                // Subtract lowest from highest to get max saving
                .reduce((a, b) => b - a)
        ).toFixed(2);

        billingPeriodContent = (
            <>
                <ReactMarkdown>
                    {t('upgrade_save_annual', {
                        sprintf: [`${getCurrencySymbol(currency)}${maxSaving}`],
                    })}
                </ReactMarkdown>
                <BillingPeriodSwitcher selectedPeriod={billingPeriod} onChange={setBillingPeriod} />
            </>
        );

        mainContent = (
            <>
                <div className="wrapper">
                    <PricingColumns>
                        <FreePricingColumn currency={currency}>
                            <MobileFreeLink place="columns" />
                            <FreeLink place="columns" />
                            <Link className="btn btn-outline PricingLink" to="#free-info">
                                {t('learn_more')}
                            </Link>
                        </FreePricingColumn>
                        <EssentialsPricingColumn
                            currency={currency}
                            prices={prices.essentials[currency] as Prices}
                        >
                            <MobilePlanLink place="columns" plan="Essentials" />
                            <PlanLink place="columns" plan="Essentials" />
                            <Link className="btn btn-outline PricingLink" to="#essentials-info">
                                {t('learn_more')}
                            </Link>
                        </EssentialsPricingColumn>
                        <PlusPricingColumn
                            currency={currency}
                            prices={prices.plus[currency] as Prices}
                        >
                            <MobilePlanLink place="columns" plan="Plus" />
                            <PlanLink place="columns" plan="Plus" />
                            <Link className="btn btn-outline PricingLink" to="#plus-info">
                                {t('learn_more')}
                            </Link>
                        </PlusPricingColumn>
                        <ProPricingColumn
                            currency={currency}
                            prices={prices.pro[currency] as Prices}
                        >
                            <MobilePlanLink place="columns" plan="Pro" />
                            <PlanLink place="columns" plan="Pro" />
                            <Link className="btn btn-outline PricingLink" to="#pro-info">
                                {t('learn_more')}
                            </Link>
                        </ProPricingColumn>
                    </PricingColumns>
                </div>
            </>
        );
    }

    return (
        <section className="mb-10">
            <div className="wrapper text-center">{billingPeriodContent}</div>
            {mainContent}
            <PricingComparison
                freeLink={<FreeLink place="columns" />}
                essentialsLink={<PlanLink place="columns" plan="Essentials" />}
                plusLink={<PlanLink place="columns" plan="Plus" />}
                proLink={<PlanLink place="columns" plan="Pro" />}
                className="PricingTableComparison"
            />
            <div className="wrapper">
                <h2 id="pro-info" className="PricingInfoHeader pro">
                    <strong>{t('plan_info_type_pro')}</strong>. {t('plan_info_whats_included')}:
                    <Button
                        as="a"
                        href="https://notes.fieldmargin.com/?redirect=billing%2Fupgrade"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t('choose_plan')}
                    </Button>
                </h2>
                <ProInfo />
                <h2 id="plus-info" className="PricingInfoHeader plus">
                    <strong>{t('plan_info_type_plus')}</strong>. {t('plan_info_whats_included')}:
                    <Button
                        as="a"
                        href="https://notes.fieldmargin.com/?redirect=billing%2Fupgrade"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t('choose_plan')}
                    </Button>
                </h2>
                <PlusInfo />
                <h2 id="essentials-info" className="PricingInfoHeader essentials">
                    <strong>{t('plan_info_type_essentials')}</strong>.{' '}
                    {t('plan_info_whats_included')}:
                    <Button
                        as="a"
                        href="https://notes.fieldmargin.com/?redirect=billing%2Fupgrade"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t('choose_plan')}
                    </Button>
                </h2>
                <EssentialsInfo />
                <h2 id="free-info" className="PricingInfoHeader free">
                    <strong>{t('plan_info_type_free')}</strong>. {t('plan_info_whats_included')}:
                    <Button
                        as="a"
                        href="https://notes.fieldmargin.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t('choose_plan')}
                    </Button>
                </h2>
                <FreeInfo />
            </div>
        </section>
    );
};
